import { useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Chip, Icon, Tooltip, Body } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { SeverityTag } from 'components/SeverityTag/SeverityTag';
import { useStatefulHistory } from 'hooks/history';
import { useUrl } from 'hooks/useUrls';
import { formatDateRecentTime, sortStringOrUndefined } from 'utils/misc';
import { getConsistencyRuleReportUrl } from 'utils/router-url';
import {
  ConsistencyRuleRunResult,
  RuleOperatorLabel,
} from '../consistencyReportTypes';
import { ConsistencyReportDetail } from '../hooks/useConsistencyReport';

const RuleDescription = styled(Body)`
  color: var(--cogs-greyscale-grey7);
`;

const filterRows = (
  rules: ConsistencyRuleRunResult[],
  search: string
): ConsistencyRuleRunResult[] => {
  if (!search.trim()) return rules;
  const term = search.trim().toLowerCase();
  return rules.filter((r) =>
    [
      r.ruleName,
      r.errorMessage,
      r.sourceSchemaFilterName,
      r.sourceAttribute,
      r.targetSchemaFilterName,
      r.targetAttribute,
    ].some((v) => v && v.toLowerCase().includes(term))
  );
};

export const useConsistencyReportView = (report: ConsistencyReportDetail) => {
  const [search, setSearch] = useState('');
  const history = useStatefulHistory();
  const tenant = useUrl.Tenant();
  const digitalTwinId = useUrl.DigitalTwinId();

  const filteredRules = filterRows(report.ruleResults, search);
  const failedRuleCount = filteredRules.filter((r) => r.totalFailed > 0).length;
  const totalFailed = filteredRules.reduce(
    (n: number, r: ConsistencyRuleRunResult) => n + r.totalFailed,
    0
  );

  const goToRule = (ruleResult: ConsistencyRuleRunResult) => {
    history.pushStateful(
      getConsistencyRuleReportUrl(
        tenant,
        digitalTwinId,
        report.reportId,
        ruleResult.ruleId
      )
    );
  };

  const columns: ColumnsType<ConsistencyRuleRunResult> = [
    {
      title: 'Last updated',
      key: 'runAt',
      width: '10%',
      render: () => formatDateRecentTime(report.runAt),
    },
    {
      title: 'Rule number',
      key: 'customerDefinedId',
      width: '10%',
      sorter: (a: ConsistencyRuleRunResult, b: ConsistencyRuleRunResult) =>
        sortStringOrUndefined(a.customerDefinedId, b.customerDefinedId),
      render: (_value: unknown, record: ConsistencyRuleRunResult) =>
        record.customerDefinedId ?? '-',
    },
    {
      title: 'Error message',
      key: 'errorMessage',
      width: '20%',
      sorter: (a: ConsistencyRuleRunResult, b: ConsistencyRuleRunResult) =>
        sortStringOrUndefined(a.errorMessage, b.errorMessage),
      render: (_value: unknown, record: ConsistencyRuleRunResult) => (
        <Tooltip content={record.ruleName}>
          <span>{record.errorMessage ?? '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Comparison',
      key: 'comparison',
      width: '30%',
      render: (_value: unknown, record: ConsistencyRuleRunResult) => (
        <RuleDescription size="x-small">
          {record.sourceSchemaFilterName}.{record.sourceAttribute}{' '}
          {RuleOperatorLabel[record.operator]} {record.targetSchemaFilterName}.
          {record.targetAttribute}
        </RuleDescription>
      ),
    },
    {
      title: 'No. failed items',
      key: 'totalFailed',
      width: '15%',
      sorter: (a: ConsistencyRuleRunResult, b: ConsistencyRuleRunResult) =>
        a.totalFailed - b.totalFailed,
      render: (_value: unknown, record: ConsistencyRuleRunResult) => {
        if (record.totalValid === 0 && record.totalFailed === 0) {
          return (
            <Chip type="neutral" label="No data to validate." size="small" />
          );
        }
        return record.totalFailed > 0 ? (
          <Chip
            type="danger"
            label={`${record.totalFailed} failed`}
            size="small"
          />
        ) : (
          <Chip type="success" label="0 failed" size="small" />
        );
      },
    },
    {
      title: 'Severity',
      key: 'severity',
      width: '10%',
      sorter: (a: ConsistencyRuleRunResult, b: ConsistencyRuleRunResult) =>
        sortStringOrUndefined(a.severity, b.severity),
      render: (_value: unknown, record: ConsistencyRuleRunResult) =>
        record.severity ? <SeverityTag severity={record.severity} /> : null,
    },
    {
      key: 'icon',
      width: '5%',
      render: () => <Icon type="ArrowRight" />,
    },
  ];

  return {
    search,
    setSearch,
    filteredRules,
    failedRuleCount,
    totalFailed,
    goToRule,
    columns,
  };
};
